import React, { useState, useEffect, useCallback } from 'react';
import api from '../hooks/api';

const ENTITY_TYPES = ['branch', 'department', 'user', 'employee', 'device'];
const PAGE_SIZE = 50;

function ActionBadge({ action }) {
  return <span className={`badge badge-action-${action}`}>{action}</span>;
}

export default function AuditPage() {
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [filterType, setFilterType] = useState('');
  const [filterUser, setFilterUser] = useState('');

  const loadUsers = useCallback(async () => {
    setUsers(await api.get('/api/users'));
  }, []);

  const loadLogs = useCallback(async (off = 0, append = false) => {
    const params = new URLSearchParams({ limit: PAGE_SIZE, offset: off });
    if (filterType) params.set('entity_type', filterType);
    if (filterUser) params.set('user_id', filterUser);
    const data = await api.get('/api/audit?' + params);
    setTotal(data.total);
    setLogs(prev => append ? [...prev, ...data.logs] : data.logs);
    setOffset(off + data.logs.length);
  }, [filterType, filterUser]);

  useEffect(() => { loadUsers(); }, [loadUsers]);
  useEffect(() => { loadLogs(0, false); }, [loadLogs]);

  function handleFilter() { loadLogs(0, false); }

  async function handleExport() {
    const params = new URLSearchParams();
    if (filterType) params.set('entity_type', filterType);
    if (filterUser) params.set('user_id', filterUser);
    const today = new Date().toISOString().slice(0, 10);
    try { await api.download('/api/audit/export' + (params.toString() ? '?' + params : ''), `audit-${today}.csv`); }
    catch (err) { alert(err.message); }
  }

  function loadMore() { loadLogs(offset, true); }

  function formatTime(ts) {
    if (!ts) return '';
    const d = new Date(ts);
    return d.toLocaleString();
  }

  function formatDetails(details) {
    if (!details) return null;
    try {
      const obj = typeof details === 'string' ? JSON.parse(details) : details;
      return JSON.stringify(obj, null, 2);
    } catch {
      return details;
    }
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title" style={{ margin: 0 }}>Audit Log</h1>
        <button className="btn btn-ghost" onClick={handleExport}>Export CSV</button>
      </div>
      <div className="filters">
        <select value={filterType} onChange={e => setFilterType(e.target.value)}>
          <option value="">All Entity Types</option>
          {ENTITY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={filterUser} onChange={e => setFilterUser(e.target.value)}>
          <option value="">All Users</option>
          {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
        <button className="btn btn-ghost" onClick={handleFilter}>Apply</button>
      </div>

      <div className="timeline" style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', boxShadow: 'var(--shadow)' }}>
        {logs.map(entry => (
          <div key={entry.id} className="timeline-entry">
            <div className={`timeline-dot dot-${entry.action}`} />
            <div className="timeline-content">
              <div className="timeline-meta">
                <ActionBadge action={entry.action} />
                <span className="tag" style={{ textTransform: 'capitalize' }}>{entry.entity_type}</span>
                <span className="timeline-label">{entry.entity_label || entry.entity_id}</span>
                <span className="timeline-time">{formatTime(entry.created_at)}</span>
              </div>
              <div className="timeline-user">by {entry.user_name}</div>
              {entry.details && (
                <pre className="timeline-details">{formatDetails(entry.details)}</pre>
              )}
            </div>
          </div>
        ))}
        {logs.length === 0 && (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>No audit entries found</div>
        )}
      </div>

      {logs.length < total && (
        <div className="load-more">
          <button className="btn btn-ghost" onClick={loadMore}>Load more ({total - logs.length} remaining)</button>
        </div>
      )}
    </div>
  );
}
